'use client'

import React from 'react'

import useMergedRefs from '../../hooks/use-merge-refs'
import usePopover, {useClosePopoverOnOutsideClick} from '../../hooks/use-popover'
import usePopoverPosition from '../../hooks/use-popover-position'
import useStackedCloseOnEscape from '../../hooks/use-stacked-close-on-escape'
import {Rectangle} from '../../services/dom-observer'
import {
  PopoverAnchorPlacement,
  PositionPopoverHandler,
  defaultPlacementFallbacks,
  positionPopover,
} from '../../services/position-popover'

export interface ChildrenRenderProps<TAnchor extends HTMLElement, TPopover extends HTMLElement> {
  ref: React.RefCallback<TAnchor | null>
  close: () => void
  open: () => void
  toggle: () => void
  isOpen: boolean
  anchorRefObject: React.RefObject<TAnchor | null>
  popoverRefObject: React.RefObject<TPopover | null>
  anchorRect: Rectangle | null
  popoverRect: Rectangle | null
}

export interface PopoverRenderProps<TAnchor extends HTMLElement, TPopover extends HTMLElement> {
  ref: React.RefCallback<TPopover | null>
  close: () => void
  open: () => void
  toggle: () => void
  isOpen: boolean
  anchorRefObject: React.RefObject<TAnchor | null>
  popoverRefObject: React.RefObject<TPopover | null>
  anchorRect: Rectangle | null
  popoverRect: Rectangle | null
  style: React.CSSProperties
}

export interface Props<TAnchor extends HTMLElement, TPopover extends HTMLElement> {
  /**
   * Popover state can be controlled from outside or left uncontrolled.
   * If isOpen is set to undefined an internal state will handle opening and closing popovers automatically.
   */
  isOpen?: boolean
  /**
   * The anchor element which will be used to position the popover.
   * Can be a React element or a function that receives the popover state and a ref to the anchor element.
   * In case of a React element it has to support ref forwarding to the actual DOM element.
   */
  children:
    | React.ReactElement<{
        ref: React.Ref<TAnchor>
      }>
    | ((params: ChildrenRenderProps<TAnchor, TPopover>) => React.ReactElement)
  /**
   * The popover element.
   * Can be a React element or a function that receives the popover state and a ref to the popover element.
   * In case of a React element it has to support ref forwarding to the actual DOM element.
   * In case of a function make sure to forward the ref, style, placement and shift props accordingly.
   */
  popover:
    | React.ReactElement<{
        ref: React.Ref<TPopover>
        style: React.CSSProperties
      }>
    | ((params: PopoverRenderProps<TAnchor, TPopover>) => React.ReactElement)
  /** Preferred placement of the tooltip relative to its anchor. Only works if default position handler is used.  */
  placement?: PopoverAnchorPlacement
  /** Margin around the viewport. Restricts where the popover can be positioned. Only works if default position handler is used. */
  viewportMargin?: number
  /** Distance to anchor. Only works if default position handler is used.  */
  distanceToAnchor?: number
  /** Perpedicular distance to anchor position. Only works if default position handler is used. */
  shiftFromAnchor?: number
  /** Customizable positioning handler. */
  positionHandler?: PositionPopoverHandler
  /**
   * If used controlled this callback is called when isOpen should change.
   * If used uncontrolled this callback is called when isOpen changed.
   */
  onOpenChange?: (isOpen: boolean) => void
  /** If true closes popover when user clicks outside of popover and child content. */
  closeOnOutsideClick?: boolean
  /** Closes the popover on Escape key press if the popover is the topmost element. */
  closeOnEscape?: boolean
  /**
   * Provide a ref to get access to the popover element.
   * This is completely optional and only needed for use cases
   * where access to the popover element is needed outside of the PopoverHandler and its render functions.
   */
  popoverRef?: React.Ref<TPopover>
  /**
   * Provide a ref to get access to the anchor element.
   * This is completely optional and only needed for use cases
   * where access to the anchor element is needed outside of the PopoverHandler and its render functions.
   */
  anchorRef?: React.Ref<TAnchor>
}

/**
 * Handles opening/closing popovers and their positioning usually around some anchor element.
 * The popover can be controlled from outside via isOpen or left uncontrolled.
 */
const PopoverHandler = <TAnchor extends HTMLElement, TPopover extends HTMLElement>({
  children,
  popover,
  isOpen,
  onOpenChange,
  placement = PopoverAnchorPlacement.bottomStart,
  viewportMargin = 12,
  distanceToAnchor = 0,
  shiftFromAnchor = 0,
  positionHandler,
  closeOnOutsideClick = true,
  closeOnEscape = true,
  popoverRef,
  anchorRef,
}: Props<TAnchor, TPopover>) => {
  const {
    ref: innerPopoverRef,
    renderPopover,
    isOpen: isPopoverOpen,
    openPopover,
    closePopover,
    togglePopover,
  } = usePopover({
    isOpen,
    onOpenChange,
  })

  const {
    popoverRef: popoverPositionRef,
    anchorRef: innerAnchorRef,
    popoverRefObject,
    anchorRefObject,
    anchorRect,
    popoverRect,
    popoverStyle,
  } = usePopoverPosition<TAnchor, TPopover>({
    isOpen: isPopoverOpen,
    positionHandler:
      positionHandler ??
      positionPopover(
        [placement, ...defaultPlacementFallbacks[placement]],
        distanceToAnchor,
        shiftFromAnchor,
        viewportMargin
      ),
  })

  const finalAnchorRef = useMergedRefs(anchorRef, innerAnchorRef)
  const finalPopoverRef = useMergedRefs(popoverRef, innerPopoverRef, popoverPositionRef)

  useClosePopoverOnOutsideClick(
    anchorRefObject,
    popoverRefObject,
    closePopover,
    closeOnOutsideClick && isPopoverOpen
  )

  useStackedCloseOnEscape(isPopoverOpen, closePopover, closeOnEscape)

  const childElement =
    typeof children === 'function'
      ? children({
          ref: finalAnchorRef,
          close: closePopover,
          open: openPopover,
          toggle: togglePopover,
          isOpen: isPopoverOpen,
          anchorRefObject,
          popoverRefObject,
          anchorRect,
          popoverRect,
        })
      : React.cloneElement(children, {ref: finalAnchorRef})

  const popoverElement =
    typeof popover === 'function'
      ? popover({
          ref: finalPopoverRef,
          close: closePopover,
          open: openPopover,
          toggle: togglePopover,
          isOpen: isPopoverOpen,
          anchorRefObject,
          popoverRefObject,
          anchorRect,
          popoverRect,
          style: popoverStyle,
        })
      : React.cloneElement(popover, {
          ref: finalPopoverRef,
          style: popoverStyle,
        })

  return (
    <>
      {childElement}
      {renderPopover(popoverElement)}
    </>
  )
}

export default PopoverHandler
